import React, { useState } from 'react';
import { useOrganization } from '../contexts/OrganizationContext';
import { CreateProjectModal } from './CreateProjectModal';

export function ProjectSidebar() {
  const { currentOrganization, currentProject, projects, setCurrentProject, loading } = useOrganization();
  const [showCreateModal, setShowCreateModal] = useState(false);

  if (loading) {
    return (
      <div className="w-64 bg-white border-r border-gray-200 p-4">
        <div className="space-y-2">
          <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
          <div className="h-8 bg-gray-200 rounded animate-pulse"></div>
          <div className="h-8 bg-gray-200 rounded animate-pulse"></div>
        </div>
      </div>
    );
  }

  if (!currentOrganization) {
    return (
      <div className="w-64 bg-white border-r border-gray-200 p-4">
        <p className="text-sm text-gray-500">Select an organization to view projects</p>
      </div>
    );
  }

  return (
    <>
      <div className="w-64 bg-white border-r border-gray-200 flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Projects</h2>
        </div>

        {/* Projects List */}
        <div className="flex-1 overflow-y-auto p-2">
          {projects.length === 0 ? (
            <div className="p-4 text-center">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="mt-2 text-sm text-gray-500">No projects yet</p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="mt-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                Create your first project
              </button>
            </div>
          ) : (
            <div className="space-y-1">
              {projects.map((project: any) => (
                <button
                  key={project.id}
                  onClick={() => setCurrentProject(project)}
                  className={`w-full flex items-center px-3 py-2 text-sm rounded-md transition-colors ${
                    currentProject?.id === project.id
                      ? 'bg-blue-100 text-blue-700 font-medium'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <div
                    className="w-3 h-3 rounded-full mr-2 flex-shrink-0"
                    style={{ backgroundColor: project.color || '#3B82F6' }}
                  />
                  <span className="flex-1 truncate text-left">{project.name}</span>
                  {project.contextCount !== undefined && (
                    <span className="text-xs text-gray-500 ml-2">{project.contextCount}</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Create Project Button */}
        <div className="p-3 border-t border-gray-200">
          <button
            onClick={() => setShowCreateModal(true)}
            className="w-full flex items-center justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            New Project
          </button>
        </div>

        {/* Quick Stats */}
        {currentProject && (
          <div className="p-3 bg-gray-50 border-t border-gray-200">
            <div className="text-xs text-gray-500 mb-1">Current Project</div>
            <div className="font-medium text-sm text-gray-900 truncate">{currentProject.name}</div>
            {currentProject.description && (
              <div className="text-xs text-gray-500 mt-1 truncate">{currentProject.description}</div>
            )}
          </div>
        )}
      </div>

      {showCreateModal && (
        <CreateProjectModal onClose={() => setShowCreateModal(false)} />
      )}
    </>
  );
}
